import { EnvVar } from '@vault/shared';
import { Copy, Edit, Trash2 } from 'lucide-react';
import { copyToClipboard } from '../utils/clipboard';

interface EnvVarDisplayProps {
  envVars: EnvVar[];
  onEditEnvVar?: (envVar: EnvVar) => void;
  onDeleteEnvVar?: (envVar: EnvVar) => void;
}

export default function EnvVarDisplay({ envVars, onEditEnvVar, onDeleteEnvVar }: EnvVarDisplayProps) {
  const handleCopyVariable = (envVar: EnvVar) => {
    copyToClipboard(`${envVar.key}=${envVar.value}`, `Copied ${envVar.key}!`);
  };

  const handleCopyAll = () => {
    const content = envVars.map(env => `${env.key}=${env.value}`).join('\n');
    copyToClipboard(content, 'All variables copied in .env format!');
  };

  return (
    <div className="p-6 overflow-y-auto">
      {/* Header with actions */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Environment Variables
        </h3>
        <button
          onClick={handleCopyAll}
          className="btn-secondary"
        >
          <Copy className="w-4 h-4 mr-2" />
          Copy All
        </button>
      </div>

      {/* Variables List in Markdown Style */}
      <div className="bg-gray-900 rounded-lg overflow-hidden">
        <div className="p-4 space-y-2 max-h-[32rem] overflow-y-auto">
          {envVars.map((envVar) => (
            <div
              key={envVar.id}
              className="flex items-center justify-between group hover:bg-gray-800 rounded px-2 py-1 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <code className="text-sm text-gray-100 font-mono break-all">
                  <span className="text-blue-300">{envVar.key}</span>
                  <span className="text-gray-400">=</span>
                  <span className="text-green-300">{envVar.value}</span>
                </code>
              </div>
              
              <div className="flex items-center space-x-1 ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => handleCopyVariable(envVar)}
                  className="p-1 text-gray-400 hover:text-gray-200 rounded"
                  title="Copy this variable"
                >
                  <Copy className="w-3 h-3" />
                </button>
                {onEditEnvVar && (
                  <button
                    onClick={() => onEditEnvVar(envVar)}
                    className="p-1 text-gray-400 hover:text-blue-400 rounded"
                    title="Edit variable"
                  >
                    <Edit className="w-3 h-3" />
                  </button>
                )}
                {onDeleteEnvVar && (
                  <button
                    onClick={() => onDeleteEnvVar(envVar)}
                    className="p-1 text-gray-400 hover:text-red-400 rounded"
                    title="Delete variable"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Info footer */}
      <div className="mt-4 text-center">
        <p className="text-sm text-gray-500">
          {envVars.length} variable{envVars.length !== 1 ? 's' : ''} • Hover over a line to see actions
        </p>
      </div>
    </div>
  );
}